import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';

export default function PlaceRank() {
  const checkAuth = useAuthGuard();
  const [places, setPlaces] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [keywords, setKeywords] = useState<string[]>(['', '', '', '', '']);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'list' | 'add' | 'detail'>('list');
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadPlaces(); }, []);

  const loadPlaces = async () => {
    try {
      const res = await client.get('/place-rank/places');
      setPlaces(res.data.places || []);
    } catch {}
  };

  const handleAddPlace = async () => {
    if (!newUrl.trim()) return setError('URL을 입력해주세요');
    setAdding(true); setError('');
    try {
      await client.post('/place-rank/places', { place_url: newUrl.trim() });
      setNewUrl('');
      await loadPlaces();
      setView('list');
    } catch (e: any) {
      setError(e.response?.data?.detail || '등록 실패');
    } finally { setAdding(false); }
  };

  const handleSelectPlace = async (p: any) => {
    setSelectedPlace(p);
    setKeywords(['', '', '', '', '']);
    setResults([]);
    setView('detail');
    try {
      const res = await client.get(`/place-rank/places/${p.id}/results`);
      const savedResults = res.data.results || [];
      setResults(savedResults);
      if (savedResults.length > 0) {
        const savedKeywords = savedResults.map((r: any) => r.keyword || '');
        const padded = [...savedKeywords, ...Array(Math.max(0, 5 - savedKeywords.length)).fill('')];
        setKeywords(padded);
      }
    } catch {}
  };

  const handleToggleAutoTrack = async (placeId: number) => {
    try {
      await client.post(`/place-rank/places/${placeId}/auto-track`);
      await loadPlaces();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
          window.location.href = '/pricing';
        }
      } else {
        alert('변경 실패');
      }
    }
  };

  const handleDeletePlace = async (placeId: number) => {
    if (!window.confirm('정말 삭제하시겠어요?')) return;
    try {
      await client.delete(`/place-rank/places/${placeId}`);
      await loadPlaces();
    } catch { alert('삭제 실패'); }
  };

  const handleCheck = async () => {
    if (!checkAuth()) return;
    const valid = keywords.filter(k => k.trim()).slice(0, 10);
    if (!valid.length) return;
    setLoading(true); setError('');
    setResults(prev => {
      const old = prev.filter(r => !valid.includes(r.keyword));
      return [...old, ...valid.map(k => ({ keyword: k, status: 'loading' }))];
    });
    try {
      const res = await client.post('/place-rank/search', {
        place_url: selectedPlace.place_url,
        keywords: valid,
        place_db_id: selectedPlace.id,
      });
      const results = res.data.results || [];
      setResults(prev => prev.map(r => {
        const found = results.find((x: any) => x.keyword === r.keyword);
        if (!found) return r;
        if (found.status === 'error') return { keyword: r.keyword, status: 'error', errorMsg: found.error || '조회 실패' };
        return { ...found, status: 'done' };
      }));
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        setError(detail);
        setResults(prev => prev.map(r => valid.includes(r.keyword) ? { keyword: r.keyword, status: 'error', errorMsg: detail } : r));
        if (detail.includes('플랜') || detail.includes('시간')) {
          if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
            window.location.href = '/pricing';
          }
        } else {
          alert(detail);
        }
      } else {
        setResults(prev => prev.map(r => valid.includes(r.keyword) ? { keyword: r.keyword, status: 'error', errorMsg: detail || '조회 실패' } : r));
      }
    } finally { setLoading(false); }
  };

  const handleDeleteKeyword = async (keyword: string) => {
    if (!window.confirm(`"${keyword}" 키워드 데이터를 삭제할까요?`)) return;
    try {
      await client.delete(`/place-rank/places/${selectedPlace.id}/keywords/${encodeURIComponent(keyword)}`);
      setResults(prev => prev.filter(r => r.keyword !== keyword));
      setKeywords(prev => prev.map(k => k === keyword ? '' : k));
    } catch { alert('삭제 실패'); }
  };

  const handleRefreshOne = async (keyword: string) => {
    setResults(prev => prev.map(r => r.keyword === keyword ? { ...r, status: 'loading' } : r));
    try {
      const res = await client.post('/place-rank/search', {
        place_url: selectedPlace.place_url,
        keywords: [keyword],
        place_db_id: selectedPlace.id,
      });
      const found = (res.data.results || []).find((x: any) => x.keyword === keyword);
      setResults(prev => prev.map(r =>
        r.keyword === keyword ? (found ? { ...found, status: 'done' } : { ...r, status: 'error' }) : r
      ));
    } catch {
      setResults(prev => prev.map(r =>
        r.keyword === keyword ? { ...r, status: 'error' } : r
      ));
    }
  };

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
    label: { display: 'block', color: '#8892A4', fontSize: 12, marginBottom: 6 } as any,
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 12px', color: '#E8ECF4', fontSize: 14, boxSizing: 'border-box' as const, marginBottom: 12 } as any,
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' } as any,
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 8, padding: '8px 16px', color: '#8892A4', fontSize: 13, cursor: 'pointer', marginRight: 8 } as any,
    btnBlue: { background: '#1A3A5C', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#378ADD', fontSize: 12, fontWeight: 600, cursor: 'pointer' } as any,
    btnSmall: { background: '#1A2A1A', border: '0.5px solid #2A4A2A', borderRadius: 6, padding: '4px 10px', color: '#00D4AA', fontSize: 11, cursor: 'pointer' } as any,
  };

  const RankCard = ({ result }: { result: any }) => (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: result.found ? 16 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{result.keyword}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {result.status !== 'loading' && (
            <>
              <button style={s.btnSmall} onClick={() => handleRefreshOne(result.keyword)} disabled={loading}>갱신</button>
              <button style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 6, padding: '4px 10px', color: '#F56565', fontSize: 11, cursor: 'pointer' }}
                onClick={() => handleDeleteKeyword(result.keyword)}>삭제</button>
            </>
          )}
          {result.status === 'loading' ? (
            <div style={{ color: '#8892A4', fontSize: 13 }}>조회 중...</div>
          ) : result.status === 'error' ? (
            <div style={{ color: '#F56565', fontSize: 13 }}>{result.errorMsg || '조회 실패'}</div>
          ) : result.found ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {result.best_rank && (
                <div style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', textAlign: 'center' as const }}>
                  <div style={{ fontSize: 10, color: '#4A5568' }}>최고</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#F6AD55' }}>{result.best_rank}위</div>
                </div>
              )}
              {result.rank_change !== undefined && result.rank_change !== null && (
                <div style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', textAlign: 'center' as const }}>
                  <div style={{ fontSize: 10, color: '#4A5568' }}>변동</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: result.rank_change < 0 ? '#00D4AA' : result.rank_change > 0 ? '#F56565' : '#8892A4' }}>
                    {result.rank_change < 0 ? `▲${Math.abs(result.rank_change)}` : result.rank_change > 0 ? `▼${result.rank_change}` : '유지'}
                  </div>
                </div>
              )}
              <div style={{ background: '#0D1F35', borderRadius: 6, padding: '6px 14px', textAlign: 'center' as const }}>
                <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#00D4AA' }}>{result.rank}위</div>
              </div>
              <div style={{ background: '#1A2035', borderRadius: 6, padding: '6px 14px', textAlign: 'center' as const }}>
                <div style={{ fontSize: 10, color: '#4A5568' }}>전체</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#8892A4' }}>{result.total_results?.toLocaleString()}개</div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#F56565', fontSize: 13 }}>100위 밖</div>
          )}
        </div>
      </div>



      {result.items && result.items.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 8 }}>상위 {result.items.length}위 목록</div>
          <div style={{ maxHeight: 200, overflowY: 'auto' as const }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' as const }}>
              <thead>
                <tr style={{ background: '#1A2035' }}>
                  {['순위', '업체명'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left' as const, color: '#4A5568', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.items.map((item: any) => {
                  const isMe = item.id === result.place_id;
                  return (
                    <tr key={item.rank} style={{ borderBottom: '0.5px solid #1E2535', background: isMe ? '#00D4AA11' : 'transparent' }}>
                      <td style={{ padding: '6px 10px', color: isMe ? '#00D4AA' : item.rank <= 3 ? '#F6AD55' : '#8892A4', fontWeight: 700, fontSize: 13 }}>{item.rank}</td>
                      <td style={{ padding: '6px 10px', color: isMe ? '#00D4AA' : '#E8ECF4', fontSize: 12, fontWeight: isMe ? 700 : 400 }}>{item.name}{isMe ? ' ⭐' : ''}</td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div>
        {view === 'list' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>플레이스 순위 조회</h2>
                <p style={{ color: '#8892A4', fontSize: 12, margin: 0 }}>플레이스를 등록하고 키워드별 순위를 확인하세요</p>
              </div>
              <button style={s.btn} onClick={() => setView('add')}>+ 플레이스 등록</button>
            </div>
            {places.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
                <div style={{ color: '#8892A4', fontSize: 14, marginBottom: 12 }}>등록된 플레이스가 없어요</div>
                <button style={s.btn} onClick={() => setView('add')}>첫 플레이스 등록하기</button>
              </div>
            ) : (
              places.map((p: any) => (
                <div key={p.id} style={s.card}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    {p.thumbnail ? (
                      <img src={p.thumbnail} alt={p.name} style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover' as const, flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 80, height: 80, borderRadius: 8, background: '#0F1117', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 28 }}>📍</span>
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name || '플레이스'}</div>
                      <div style={{ color: '#4A5568', fontSize: 12, marginBottom: 8 }}>{p.category || ''}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                        <a href={p.place_url} target="_blank" rel="noreferrer" style={{ ...s.btnGhost, textDecoration: 'none', fontSize: 12, padding: '6px 14px' }}>바로가기</a>
                        <button style={s.btnBlue} onClick={() => handleSelectPlace(p)}>분석하기</button>
                        <button style={{ background: p.auto_track ? '#0F2A22' : '#1A2A1A', border: `0.5px solid ${p.auto_track ? '#2A4A2A' : '#2A3A2A'}`, borderRadius: 8, padding: '6px 14px', color: p.auto_track ? '#00D4AA' : '#8892A4', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => handleToggleAutoTrack(p.id)}>
                          {p.auto_track ? '🟢 자동추적 ON' : '자동순위추적'}
                        </button>
                        <button style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 8, padding: '6px 14px', color: '#F56565', fontSize: 12, cursor: 'pointer' }}
                          onClick={() => handleDeletePlace(p.id)}>삭제</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {view === 'add' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setError(''); }}>← 뒤로</button>
              <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: 0 }}>플레이스 등록</h2>
            </div>
            <div style={s.card}>
              <label style={s.label}>네이버 플레이스 URL</label>
              <input style={s.input} value={newUrl} onChange={e => setNewUrl(e.target.value)}
                placeholder="https://map.naver.com/p/entry/place/1234567890" />
              <p style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>URL만 입력하면 상호명, 카테고리, 썸네일을 자동으로 가져와요</p>
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <button style={s.btn} onClick={handleAddPlace} disabled={adding}>
                {adding ? '정보 수집 중...' : '등록하기'}
              </button>
            </div>
          </>
        )}

        {view === 'detail' && selectedPlace && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setResults([]); }}>← 뒤로</button>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 2px' }}>{selectedPlace.name || '플레이스'}</h2>
                <div style={{ color: '#8892A4', fontSize: 12 }}>{selectedPlace.category || ''}</div>
              </div>
            </div>

            <div style={s.card}>
              <label style={s.label}>검색 키워드 (최대 10개)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {keywords.map((kw, i) => (
                  <input key={i} style={{ ...s.input, margin: 0 }} value={kw}
                    onChange={e => { const u = [...keywords]; u[i] = e.target.value; setKeywords(u); }}
                    placeholder={`키워드 ${i + 1}`} />
                ))}
              </div>
              {keywords.length < 10 && (
                <button style={{ ...s.btnGhost, marginBottom: 12 }} onClick={() => setKeywords([...keywords, ''])}>+ 키워드 추가</button>
              )}
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <button style={s.btn} onClick={handleCheck} disabled={loading}>
                {loading ? '조회 중...' : '순위 조회'}
              </button>
            </div>

            {results.map((r, i) => <RankCard key={i} result={r} />)}
          </>
        )}
      </div>
    </Layout>
  );
}
